import React, { useState, useMemo, useEffect, useRef } from 'react';
// import { FieldGroup } from '../../../libs/formLib';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { FormFeedback, FormGroup, Label, Alert } from 'reactstrap';
import { isEmpty, debounce } from 'lodash';
import BZAPIService from '../../../services/BZAPIService';

const selectStyles = {
  option: (styles, { isDisabled, isFocused, isSelected }) => {
    return {
      ...styles,
      color: isFocused ? 'white' : isSelected ? 'white' : 'black'
    };
  },
  menuPortal: base => ({ ...base, zIndex: 9999 })
};

const CMDBCompanyCheck = ({ snCompanySysIdOptions, snCompanySysId, setSnCompanySysId, isSubmitted }) => {
  const [searchOptions, setSearchOptions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const warmupExecuted = useRef(false);

  // Warmup effect: Make a background request to warm up the Lambda
  // This runs once on component mount and is completely transparent to the user
  useEffect(() => {
    if (warmupExecuted.current) return;
    warmupExecuted.current = true;

    const performWarmup = async () => {
      try {
        // Use a short, common query string for warmup
        await BZAPIService.integrationServiceNowCompaniesSearch('aa');
        // Results are intentionally ignored - this is just to warm up the Lambda
      } catch (error) {
        // Silently log the error - warmup failures should not affect user experience
        console.debug('ServiceNow company search warmup failed:', error);
      }
    };

    performWarmup();
  }, []);

  const debouncedSearch = useMemo(
    () =>
      debounce(async inputValue => {
        if (!inputValue || inputValue.length < 2) {
          setSearchOptions([]);
          return;
        }

        setIsSearching(true);
        setSearchError(null);

        try {
          const response = await BZAPIService.integrationServiceNowCompaniesSearch(inputValue);
          if (response && response.companies) {
            setSearchOptions(response.companies);
          } else {
            setSearchOptions([]);
          }
        } catch (error) {
          console.error('Error searching companies:', error);
          setSearchError('Failed to search companies');
          setSearchOptions([]);
        } finally {
          setIsSearching(false);
        }
      }, 300),
    []
  );

  const handleInputChange = inputValue => {
    debouncedSearch(inputValue);
  };

  const handleSelectionChange = selectedOption => {
    console.log('Selection changed:', selectedOption);
    setSnCompanySysId(selectedOption);
  };

  return (
    <FormGroup data-testid={'vendor-sys-id'}>
      <Label>
        Vendor System ID (ServiceNow)<span style={{ color: '#bd472a' }}>*</span>
      </Label>
      <Select
        id="companies"
        name="id-select"
        menuPortalTarget={document.body}
        menuPosition="fixed"
        getOptionLabel={option => (isEmpty(option) ? 'Select...' : `${option.label} (${option.value})`)}
        value={snCompanySysId}
        onChange={handleSelectionChange}
        options={searchOptions}
        onInputChange={handleInputChange}
        isLoading={isSearching}
        placeholder="Type to search companies…"
        isClearable
        isSearchable
        cacheOptions={false}
        defaultOptions={false}
        noOptionsMessage={({ inputValue }) => {
          if (!inputValue || inputValue.length < 2) {
            return 'Type at least 2 characters to search';
          }
          return isSearching ? 'Searching...' : 'No results found';
        }}
        theme={theme => ({
          ...theme,
          borderRadius: 5,
          colors: {
            ...theme.colors,
            text: 'white',
            primary25: '#253746',
            primary: '#bd472a',
            neutral10: '#bd472a',
            dangerLight: '#bf6e4c'
          }
        })}
        styles={selectStyles}
        filterOption={() => true}
      />
      {searchError && <Label className="text-danger">{searchError}</Label>}
      <FormFeedback className={isSubmitted && !snCompanySysId?.value?.length ? 'd-block' : ''} type="invalid">
        <Alert color="danger" fade={false}>
          Please select an ID.
        </Alert>
      </FormFeedback>
    </FormGroup>
  );
};
CMDBCompanyCheck.propTypes = {
  snCompanySysIdOptions: PropTypes.object, // Legacy prop, now unused but kept for backward compatibility
  snCompanySysId: PropTypes.object,
  setSnCompanySysId: PropTypes.func,
  isSubmitted: PropTypes.bool,
  loading: PropTypes.bool
};

export default CMDBCompanyCheck;
